import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ElementRef,
    ViewChild,
    AfterViewInit,
    OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import * as d3 from 'd3';
import { OrgWidgetData, OrgWidgetEntity } from '../../models/org-widget.models';
import { OrgDialogService } from '../../../org-board/services/org-dialog.service';

/** A published policy with acknowledgment data extracted from OrgWidgetData */
interface AckPolicy {
    key: string;
    name: string;
    category: string;
    requiresAcknowledgment: boolean;
    totalRequired: number;
    totalAcknowledged: number;
    completionPct: number;
    deadlineStr: string;
    isOverdue: boolean;
}

/** Per-user policy detail in the compliance view */
interface UserPolicyDetail {
    policyKey: string;
    policyName: string;
    isComplete: boolean;
    completedAt: string;
    isOverdue: boolean;
}

/** Per-user compliance summary */
interface UserComplianceRow {
    uid: string;
    displayName: string;
    initials: string;
    avatarColor: string;
    totalRequired: number;
    acknowledgedCount: number;
    compliancePct: number;
    hasOverdue: boolean;
    expanded: boolean;
    /** Policies this user has acknowledgment records for */
    policies: UserPolicyDetail[];
    /** Policies that require ack but this user has no record for */
    missingPolicies: { name: string; key: string; isOverdue: boolean }[];
}

@Component({
    selector: 'shared-policy-acknowledgment-widget',
    imports: [CommonModule, MatIconModule],
    template: `
        <div class="ack-container" *ngIf="data">
            <!-- Filter buttons -->
            <div class="summary-row">
                <div
                    class="summary-stat"
                    [class.active]="activeFilter === 'published'"
                    (click)="activeFilter = 'published'"
                >
                    <span class="stat-value">{{ publishedCount }}</span>
                    <span class="stat-label">Published</span>
                </div>
                <div
                    class="summary-stat"
                    [class.active]="activeFilter === 'requiresAck'"
                    (click)="activeFilter = 'requiresAck'"
                >
                    <span class="stat-value">{{ requiresAckCount }}</span>
                    <span class="stat-label">Require Ack</span>
                </div>
                <div
                    class="summary-stat"
                    [class.active]="activeFilter === 'acknowledged'"
                    (click)="activeFilter = 'acknowledged'"
                >
                    <span class="stat-value">{{ acknowledgedCount }}</span>
                    <span class="stat-label">Acknowledged</span>
                </div>
                <div
                    class="summary-stat"
                    *ngIf="overdueCount > 0"
                    [class.active]="activeFilter === 'overdue'"
                    (click)="activeFilter = 'overdue'"
                >
                    <span class="stat-value overdue">{{ overdueCount }}</span>
                    <span class="stat-label">Overdue</span>
                </div>
            </div>

            <!-- D3 donut chart -->
            <div class="chart-area">
                <div class="donut-wrapper">
                    <svg #donutChart></svg>
                    <div class="donut-center">
                        <span class="donut-pct">{{ overallPct }}%</span>
                        <span class="donut-sub">compliance</span>
                    </div>
                </div>

                <!-- Legend -->
                <div class="chart-legend">
                    <div class="legend-item" *ngFor="let seg of legendItems">
                        <span class="legend-color" [style.background]="seg.color"></span>
                        <span class="legend-label">{{ seg.label }}</span>
                        <span class="legend-value">{{ seg.value }}</span>
                    </div>
                </div>
            </div>

            <!-- Tab toggle: Per Policy | Per Staff Member -->
            <div class="view-tabs" *ngIf="publishedCount > 0">
                <button class="tab-btn" [class.active]="activeTab === 'policy'" (click)="activeTab = 'policy'">
                    <mat-icon>description</mat-icon> Per Policy
                </button>
                <button class="tab-btn" [class.active]="activeTab === 'user'" (click)="activeTab = 'user'">
                    <mat-icon>people</mat-icon> Per Staff Member
                </button>
            </div>

            <!-- Per-policy bar chart TAB -->
            <div class="bar-section" *ngIf="activeTab === 'policy' && displayedPolicies.length > 0">
                <div class="bar-list">
                    <div
                        class="bar-row clickable"
                        [class.no-ack]="!p.requiresAcknowledgment"
                        *ngFor="let p of displayedPolicies"
                        (click)="onPolicyBarClick(p)"
                    >
                        <div class="bar-label">
                            <span class="bar-name">{{ p.name }}</span>
                            <span class="bar-pct" *ngIf="p.requiresAcknowledgment" [class.overdue]="p.isOverdue"
                                >{{ p.completionPct }}%</span
                            >
                            <span class="bar-pct na" *ngIf="!p.requiresAcknowledgment">N/A</span>
                        </div>
                        <div class="bar-track" *ngIf="p.requiresAcknowledgment">
                            <div
                                class="bar-fill"
                                [style.width.%]="p.completionPct"
                                [style.background]="getBarColor(p.completionPct)"
                            ></div>
                        </div>
                        <div class="bar-track na-track" *ngIf="!p.requiresAcknowledgment">
                            <div class="bar-fill" style="width: 100%; background: #90a4ae;"></div>
                        </div>
                        <div class="bar-counts" *ngIf="p.requiresAcknowledgment">
                            {{ p.totalAcknowledged }}/{{ p.totalRequired }}
                            <mat-icon class="overdue-icon" *ngIf="p.isOverdue" title="Overdue">warning</mat-icon>
                        </div>
                        <div class="bar-counts na-label" *ngIf="!p.requiresAcknowledgment">No ack required</div>
                    </div>
                </div>
            </div>

            <!-- Per-user compliance TAB -->
            <div class="bar-section" *ngIf="activeTab === 'user' && userCompliance.length > 0">
                <div class="bar-list">
                    <div
                        class="user-row"
                        *ngFor="let u of filteredUserCompliance"
                        [class.expanded]="u.expanded"
                        (click)="u.expanded = !u.expanded"
                    >
                        <div class="bar-label">
                            <div class="user-identity clickable" (click)="onUserClick(u, $event)">
                                <div class="user-avatar-small" [style.background]="u.avatarColor">
                                    {{ u.initials }}
                                </div>
                                <span class="bar-name">{{ u.displayName }}</span>
                            </div>
                            <span class="bar-pct" [class.overdue]="u.hasOverdue">{{ u.compliancePct }}%</span>
                        </div>
                        <div class="bar-track">
                            <div
                                class="bar-fill"
                                [style.width.%]="u.compliancePct"
                                [style.background]="getBarColor(u.compliancePct)"
                            ></div>
                        </div>
                        <div class="bar-counts">
                            {{ u.acknowledgedCount }}/{{ u.totalRequired }} policies
                            <mat-icon class="overdue-icon" *ngIf="u.hasOverdue" title="Has overdue policies"
                                >warning</mat-icon
                            >
                            <mat-icon class="expand-icon">{{ u.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                        </div>

                        <!-- Expanded detail: which policies this user has/hasn't acknowledged -->
                        <div class="user-detail" *ngIf="u.expanded" (click)="$event.stopPropagation()">
                            <div class="user-policy-row" *ngFor="let up of u.policies">
                                <mat-icon
                                    class="ack-icon"
                                    [class.done]="up.isComplete"
                                    [class.pending]="!up.isComplete"
                                >
                                    {{ up.isComplete ? 'check_circle' : 'radio_button_unchecked' }}
                                </mat-icon>
                                <span class="up-name">{{ up.policyName }}</span>
                                <span class="up-date" *ngIf="up.completedAt">{{ up.completedAt }}</span>
                                <span class="up-pending" *ngIf="!up.isComplete && up.isOverdue">Overdue</span>
                                <span class="up-pending pending-label" *ngIf="!up.isComplete && !up.isOverdue"
                                    >Pending</span
                                >
                            </div>
                            <!-- Policies this user hasn't started at all -->
                            <div class="user-policy-row" *ngFor="let missing of u.missingPolicies">
                                <mat-icon class="ack-icon pending">radio_button_unchecked</mat-icon>
                                <span class="up-name">{{ missing.name }}</span>
                                <span class="up-pending" *ngIf="missing.isOverdue">Overdue</span>
                                <span class="up-pending pending-label" *ngIf="!missing.isOverdue">Not started</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No user data state -->
            <div
                class="empty-state"
                *ngIf="activeTab === 'user' && userCompliance.length === 0 && requiresAckCount > 0"
            >
                <mat-icon>person_off</mat-icon>
                <span>No acknowledgment records found</span>
            </div>

            <!-- Empty state -->
            <div class="empty-state" *ngIf="publishedCount === 0">
                <mat-icon>policy</mat-icon>
                <span>No published policies</span>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .ack-container {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            /* Summary row */
            .summary-row {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
                padding: 2px;
            }

            .summary-stat {
                flex: 1;
                min-width: 60px;
                text-align: center;
                padding: 6px 4px;
                border-radius: 6px;
                background: rgba(0, 0, 0, 0.02);
                cursor: pointer;
                transition:
                    background 0.2s,
                    box-shadow 0.2s;
                user-select: none;

                &:hover:not(.active) {
                    background: rgba(0, 0, 0, 0.06);
                }

                &.active {
                    background: rgba(76, 175, 80, 0.12);
                    box-shadow: 0 0 0 1.5px rgba(76, 175, 80, 0.4);
                }
            }

            .stat-value {
                display: block;
                font-size: 22px;
                font-weight: 600;
                color: rgba(0, 0, 0, 0.87);
                line-height: 1.2;

                &.overdue {
                    color: #ef5350;
                }
            }

            .stat-label {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.45);
                text-transform: uppercase;
                letter-spacing: 0.4px;
            }

            /* Chart area */
            .chart-area {
                display: flex;
                align-items: center;
                gap: 16px;
                justify-content: center;
            }

            .donut-wrapper {
                position: relative;
                width: 130px;
                height: 130px;
                flex-shrink: 0;
            }

            .donut-wrapper svg {
                width: 130px;
                height: 130px;
            }

            .donut-center {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                text-align: center;
                pointer-events: none;
            }

            .donut-pct {
                display: block;
                font-size: 24px;
                font-weight: 700;
                color: rgba(0, 0, 0, 0.87);
                line-height: 1;
            }

            .donut-sub {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                text-transform: uppercase;
                letter-spacing: 0.3px;
            }

            /* Legend */
            .chart-legend {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }

            .legend-item {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
            }

            .legend-color {
                width: 10px;
                height: 10px;
                border-radius: 2px;
                flex-shrink: 0;
            }

            .legend-label {
                color: rgba(0, 0, 0, 0.6);
                flex: 1;
            }

            .legend-value {
                font-weight: 600;
                color: rgba(0, 0, 0, 0.87);
            }

            /* Bar chart section */
            .bar-section {
                margin-top: 4px;
            }

            .section-header {
                font-size: 11px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.54);
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 6px;
            }

            .bar-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .bar-row {
                display: flex;
                flex-direction: column;
                gap: 2px;

                &.clickable {
                    cursor: pointer;
                    padding: 4px 6px;
                    border-radius: 4px;
                    transition: background 0.15s;
                    &:hover {
                        background: rgba(0, 0, 0, 0.04);
                    }
                }
            }

            .bar-label {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
            }

            .bar-name {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.78);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                flex: 1;
                margin-right: 8px;
            }

            .bar-pct {
                font-size: 12px;
                font-weight: 600;
                color: rgba(0, 0, 0, 0.6);
                flex-shrink: 0;

                &.overdue {
                    color: #ef5350;
                }
            }

            .bar-track {
                height: 6px;
                border-radius: 3px;
                background: rgba(0, 0, 0, 0.06);
                overflow: hidden;
            }

            .bar-fill {
                height: 100%;
                border-radius: 3px;
                transition: width 0.5s ease;
            }

            .bar-counts {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                display: flex;
                align-items: center;
                gap: 4px;

                &.na-label {
                    font-style: italic;
                    color: rgba(0, 0, 0, 0.3);
                }
            }

            .bar-row.no-ack {
                opacity: 0.65;
            }

            .bar-pct.na {
                font-style: italic;
                color: rgba(0, 0, 0, 0.35);
            }

            .na-track {
                opacity: 0.4;
            }

            .overdue-icon {
                font-size: 13px;
                width: 13px;
                height: 13px;
                color: #ef5350;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 24px;
                color: rgba(0, 0, 0, 0.38);

                mat-icon {
                    font-size: 36px;
                    width: 36px;
                    height: 36px;
                }
                span {
                    font-size: 13px;
                }
            }

            /* View tabs */
            .view-tabs {
                display: flex;
                gap: 2px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                margin-bottom: 2px;
            }

            .tab-btn {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;
                padding: 6px 8px;
                border: none;
                background: none;
                cursor: pointer;
                font-size: 11px;
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 0.3px;
                color: rgba(0, 0, 0, 0.45);
                border-bottom: 2px solid transparent;
                transition: all 0.2s ease;

                mat-icon {
                    font-size: 15px;
                    width: 15px;
                    height: 15px;
                }

                &.active {
                    color: #1976d2;
                    border-bottom-color: #1976d2;
                }

                &:hover:not(.active) {
                    background: rgba(0, 0, 0, 0.03);
                }
            }

            /* User compliance rows */
            .user-row {
                display: flex;
                flex-direction: column;
                gap: 2px;
                padding: 6px 8px;
                border-radius: 6px;
                background: rgba(0, 0, 0, 0.015);
                cursor: pointer;
                transition: background 0.15s ease;

                &:hover {
                    background: rgba(0, 0, 0, 0.04);
                }
            }

            .user-identity {
                display: flex;
                align-items: center;
                gap: 6px;
                flex: 1;
                overflow: hidden;

                &.clickable {
                    cursor: pointer;
                    border-radius: 4px;
                    padding: 2px 4px;
                    margin: -2px -4px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.06);
                    }
                }
            }

            .user-avatar-small {
                width: 22px;
                height: 22px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 9px;
                font-weight: 600;
                color: #fff;
                flex-shrink: 0;
                letter-spacing: 0.3px;
            }

            .expand-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: rgba(0, 0, 0, 0.3);
                margin-left: auto;
            }

            /* Expanded user detail */
            .user-detail {
                margin-top: 4px;
                padding-top: 6px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .user-policy-row {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 2px 0 2px 28px;
                font-size: 11px;
            }

            .ack-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;

                &.done {
                    color: #43a047;
                }
                &.pending {
                    color: rgba(0, 0, 0, 0.2);
                }
            }

            .up-name {
                flex: 1;
                color: rgba(0, 0, 0, 0.78);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .up-date {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                flex-shrink: 0;
            }

            .up-pending {
                font-size: 10px;
                font-weight: 500;
                color: #ef5350;
                flex-shrink: 0;

                &.pending-label {
                    color: rgba(0, 0, 0, 0.38);
                }
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .summary-stat {
                    background: rgba(255, 255, 255, 0.04);
                    &:hover:not(.active) {
                        background: rgba(255, 255, 255, 0.08);
                    }
                    &.active {
                        background: rgba(76, 175, 80, 0.18);
                        box-shadow: 0 0 0 1.5px rgba(76, 175, 80, 0.5);
                    }
                }
                .stat-value {
                    color: rgba(255, 255, 255, 0.87);
                }
                .stat-label {
                    color: rgba(255, 255, 255, 0.45);
                }
                .donut-pct {
                    color: rgba(255, 255, 255, 0.87);
                }
                .donut-sub {
                    color: rgba(255, 255, 255, 0.38);
                }
                .legend-label {
                    color: rgba(255, 255, 255, 0.6);
                }
                .legend-value {
                    color: rgba(255, 255, 255, 0.87);
                }
                .section-header {
                    color: rgba(255, 255, 255, 0.54);
                }
                .bar-name {
                    color: rgba(255, 255, 255, 0.78);
                }
                .bar-pct {
                    color: rgba(255, 255, 255, 0.6);
                }
                .bar-track {
                    background: rgba(255, 255, 255, 0.08);
                }
                .bar-counts {
                    color: rgba(255, 255, 255, 0.38);
                    &.na-label {
                        color: rgba(255, 255, 255, 0.25);
                    }
                }
                .bar-pct.na {
                    color: rgba(255, 255, 255, 0.3);
                }
                .bar-row.clickable:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                .empty-state {
                    color: rgba(255, 255, 255, 0.38);
                }
                .tab-btn {
                    color: rgba(255, 255, 255, 0.6);
                    &.active {
                        color: #90caf9;
                        border-bottom-color: #90caf9;
                    }
                    &:hover:not(.active) {
                        background: rgba(255, 255, 255, 0.06);
                    }
                }
                .user-row {
                    background: rgba(255, 255, 255, 0.03);
                    &:hover {
                        background: rgba(255, 255, 255, 0.06);
                    }
                }
                .user-identity.clickable:hover {
                    background: rgba(255, 255, 255, 0.08);
                }
                .user-avatar-small {
                    color: #fff;
                }
                .user-detail {
                    border-top-color: rgba(255, 255, 255, 0.06);
                }
                .up-name {
                    color: rgba(255, 255, 255, 0.78);
                }
                .up-date {
                    color: rgba(255, 255, 255, 0.38);
                }
                .up-pending.pending-label {
                    color: rgba(255, 255, 255, 0.38);
                }
            }
        `,
    ],
})
export class PolicyAcknowledgmentWidgetComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() data!: OrgWidgetData;
    @ViewChild('donutChart', { static: false }) donutChartRef!: ElementRef<SVGElement>;

    constructor(private dialogService: OrgDialogService) {}

    public publishedCount = 0;
    public requiresAckCount = 0;
    public acknowledgedCount = 0;
    public overallPct = 0;
    public overdueCount = 0;
    public ackPolicies: AckPolicy[] = [];
    public allPublishedPolicies: AckPolicy[] = [];
    public activeFilter: 'published' | 'requiresAck' | 'acknowledged' | 'overdue' = 'published';
    public legendItems: { label: string; value: number; color: string }[] = [];
    public activeTab: 'policy' | 'user' = 'policy';
    public userCompliance: UserComplianceRow[] = [];
    public filteredUserCompliance: UserComplianceRow[] = [];

    private chartDrawn = false;
    private totalAcked = 0;
    private totalRequired = 0;
    private totalPending = 0;
    private totalNotRequired = 0;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.computeStats();
            if (this.donutChartRef) {
                this.drawDonut();
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.data) {
            // Slight delay so SVG element is sized
            setTimeout(() => this.drawDonut(), 0);
        }
    }

    ngOnDestroy(): void {
        // D3 cleanup
        if (this.donutChartRef?.nativeElement) {
            d3.select(this.donutChartRef.nativeElement).selectAll('*').remove();
        }
    }

    public get displayedPolicies(): AckPolicy[] {
        switch (this.activeFilter) {
            case 'published':
                return this.allPublishedPolicies;
            case 'requiresAck':
                return this.ackPolicies;
            case 'acknowledged':
                return this.ackPolicies.filter((p) => p.completionPct === 100);
            case 'overdue':
                return this.ackPolicies.filter((p) => p.isOverdue);
            default:
                return this.allPublishedPolicies;
        }
    }

    public getBarColor(pct: number): string {
        if (pct >= 80) return '#43a047';
        if (pct >= 50) return '#f9a825';
        return '#ef5350';
    }

    public onPolicyBarClick(p: AckPolicy): void {
        const policyEntity = (this.data.policies || []).find((pol) => pol.key === p.key);
        if (policyEntity) {
            this.dialogService.openPolicyDetailsDialog(p.key, policyEntity.data);
        }
    }

    public onUserClick(u: UserComplianceRow, event: Event): void {
        event.stopPropagation();
        const userEntity = (this.data.users || []).find((usr) => usr.key === u.uid);
        const userData = userEntity
            ? { ...userEntity.data, key: u.uid, name: u.displayName }
            : { key: u.uid, name: u.displayName };
        this.dialogService.openOrgUserDetailsDialog(null as any, userData);
    }

    private formatTimestamp(val: unknown): string {
        if (!val) return '';
        try {
            if (typeof val === 'object' && val !== null && 'toDate' in val) {
                return (val as { toDate: () => Date })
                    .toDate()
                    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            if (typeof val === 'object' && val !== null && 'seconds' in val) {
                return new Date((val as { seconds: number }).seconds * 1000).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                });
            }
            if (typeof val === 'object' && val !== null && '_seconds' in val) {
                return new Date((val as { _seconds: number })._seconds * 1000).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                });
            }
            const d = new Date(val as string | number);
            if (!isNaN(d.getTime()))
                return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch {
            /* ignore */
        }
        return '';
    }

    private isTimestampPast(val: unknown): boolean {
        if (!val) return false;
        try {
            let ms = 0;
            if (typeof val === 'object' && val !== null && 'toDate' in val) {
                ms = (val as { toDate: () => Date }).toDate().getTime();
            } else if (typeof val === 'object' && val !== null && 'seconds' in val) {
                ms = (val as { seconds: number }).seconds * 1000;
            } else if (typeof val === 'object' && val !== null && '_seconds' in val) {
                ms = (val as { _seconds: number })._seconds * 1000;
            } else {
                ms = new Date(val as string | number).getTime();
            }
            return ms > 0 && ms < Date.now();
        } catch {
            /* ignore */
        }
        return false;
    }

    private computeStats(): void {
        const policies = this.data.policies || [];
        const totalUsers = (this.data.users || []).length;

        // Filter to published policies only
        const published = policies.filter((p: OrgWidgetEntity) => {
            const d = p.data as Record<string, unknown>;
            if (!d) return false;
            const status = ((d['status'] as string) || '').toLowerCase();
            return status === 'published' || d['isPublished'] === true;
        });

        this.publishedCount = published.length;
        this.ackPolicies = [];
        this.totalAcked = 0;
        this.totalRequired = 0;
        this.overdueCount = 0;

        for (const policy of published) {
            const d = policy.data as Record<string, unknown>;
            const name = (d['name'] as string) || policy.key;
            const category = (d['category'] as string) || '';
            const requiresAck = d['requiresAcknowledgment'] === true;

            // Get acknowledgment stats from the org doc cache
            const stats = d['acknowledgmentStats'] as Record<string, unknown> | undefined;
            let totalReq = stats ? (stats['totalRequired'] as number) || 0 : 0;
            const totalAcked = stats ? (stats['totalAcknowledged'] as number) || 0 : 0;

            // If no stats but requires ack, use total users as required count
            if (requiresAck && totalReq === 0) {
                totalReq = totalUsers;
            }

            const pct = totalReq > 0 ? Math.round((totalAcked / totalReq) * 100) : requiresAck ? 0 : 100;
            const deadline = d['acknowledgmentDeadline'];
            const deadlineStr = this.formatTimestamp(deadline);
            const isOverdue = requiresAck && pct < 100 && this.isTimestampPast(deadline);

            if (isOverdue) this.overdueCount++;

            if (requiresAck) {
                this.totalAcked += totalAcked;
                this.totalRequired += totalReq;
            }

            this.ackPolicies.push({
                key: policy.key,
                name,
                category,
                requiresAcknowledgment: requiresAck,
                totalRequired: totalReq,
                totalAcknowledged: totalAcked,
                completionPct: pct,
                deadlineStr,
                isOverdue,
            });
        }

        // Store all published policies before filtering
        this.allPublishedPolicies = [...this.ackPolicies];

        // Filter to only policies that require acknowledgment for the ack stats
        this.ackPolicies = this.ackPolicies.filter((p) => p.requiresAcknowledgment);
        this.requiresAckCount = this.ackPolicies.length;
        this.acknowledgedCount = this.ackPolicies.filter((p) => p.completionPct === 100).length;

        // Sort all lists: overdue first, then by pct ascending
        const sortFn = (a: AckPolicy, b: AckPolicy) => {
            if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
            return a.completionPct - b.completionPct;
        };
        this.ackPolicies.sort(sortFn);
        this.allPublishedPolicies.sort(sortFn);

        // Overall percentage
        this.overallPct = this.totalRequired > 0 ? Math.round((this.totalAcked / this.totalRequired) * 100) : 0;

        // Calculate values for the donut
        this.totalPending = this.totalRequired - this.totalAcked;
        this.totalNotRequired = this.publishedCount - this.requiresAckCount;

        // Legend
        this.legendItems = [
            { label: 'Acknowledged', value: this.totalAcked, color: '#43a047' },
            { label: 'Pending', value: this.totalPending, color: '#f9a825' },
        ];
        if (this.totalNotRequired > 0) {
            this.legendItems.push({ label: 'No Ack Required', value: this.totalNotRequired, color: '#90a4ae' });
        }
        if (this.overdueCount > 0) {
            this.legendItems.push({ label: 'Overdue', value: this.overdueCount, color: '#ef5350' });
        }

        // Build per-user compliance data
        this.computeUserCompliance();
    }

    /**
     * Build per-user compliance rows from policyAcknowledgments subcollection data.
     * Each user row shows how many of the required policies they've acknowledged.
     */
    private computeUserCompliance(): void {
        const ackRecords = this.data.policyAcknowledgments || [];
        const users = this.data.users || [];
        const policiesRequiringAck = this.ackPolicies; // already filtered to requiresAcknowledgment

        if (policiesRequiringAck.length === 0) {
            this.userCompliance = [];
            this.filteredUserCompliance = [];
            return;
        }

        // Build a Set of policy keys that require ack
        const requiredPolicyKeys = new Set(policiesRequiringAck.map((p) => p.key));

        // Group acknowledgment records by user UID
        const acksByUser = new Map<string, Map<string, Record<string, unknown>>>();
        for (const ack of ackRecords) {
            const d = ack.data;
            const uid = (d['uid'] as string) || '';
            if (!uid) continue;
            const policyKey = (d['policyKey'] as string) || '';
            if (!policyKey || !requiredPolicyKeys.has(policyKey)) continue;

            if (!acksByUser.has(uid)) {
                acksByUser.set(uid, new Map());
            }
            const userMap = acksByUser.get(uid);
            if (userMap) {
                userMap.set(policyKey, d);
            }
        }

        // Avatar color palette
        const colors = [
            '#1976d2',
            '#388e3c',
            '#e64a19',
            '#7b1fa2',
            '#c2185b',
            '#00796b',
            '#5d4037',
            '#455a64',
            '#f57f17',
            '#283593',
        ];

        // Build rows for every org user
        const rows: UserComplianceRow[] = [];

        for (const user of users) {
            const ud = user.data;
            const uid = user.key || (ud['uid'] as string) || '';
            const displayName = (ud['displayName'] as string) || (ud['name'] as string) || uid;
            const nameParts = displayName.trim().split(/\s+/);
            const initials =
                nameParts.length >= 2
                    ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
                    : displayName.substring(0, 2).toUpperCase();
            const colorIndex = uid ? uid.charCodeAt(0) % colors.length : 0;

            const userAcks = acksByUser.get(uid) || new Map<string, Record<string, unknown>>();
            const policyDetails: UserPolicyDetail[] = [];
            const missingPolicies: { name: string; key: string; isOverdue: boolean }[] = [];
            let ackedCount = 0;
            let hasOverdue = false;

            for (const policy of policiesRequiringAck) {
                const ackData = userAcks.get(policy.key);
                if (ackData) {
                    const isComplete = ackData['isComplete'] === true;
                    if (isComplete) ackedCount++;
                    const isOverdue = !isComplete && policy.isOverdue;
                    if (isOverdue) hasOverdue = true;
                    policyDetails.push({
                        policyKey: policy.key,
                        policyName: policy.name,
                        isComplete,
                        completedAt: isComplete ? this.formatTimestamp(ackData['completedAt']) : '',
                        isOverdue,
                    });
                } else {
                    // No record at all — missing
                    if (policy.isOverdue) hasOverdue = true;
                    missingPolicies.push({
                        name: policy.name,
                        key: policy.key,
                        isOverdue: policy.isOverdue,
                    });
                }
            }

            const totalReq = policiesRequiringAck.length;
            const pct = totalReq > 0 ? Math.round((ackedCount / totalReq) * 100) : 0;

            rows.push({
                uid,
                displayName,
                initials,
                avatarColor: colors[colorIndex],
                totalRequired: totalReq,
                acknowledgedCount: ackedCount,
                compliancePct: pct,
                hasOverdue,
                expanded: false,
                policies: policyDetails,
                missingPolicies,
            });
        }

        // Sort: lowest compliance first, then overdue users first
        rows.sort((a, b) => {
            if (a.hasOverdue !== b.hasOverdue) return a.hasOverdue ? -1 : 1;
            return a.compliancePct - b.compliancePct;
        });

        this.userCompliance = rows;
        this.filteredUserCompliance = rows;
    }

    private drawDonut(): void {
        const svg = d3.select(this.donutChartRef.nativeElement);
        svg.selectAll('*').remove();

        const size = 130;
        const radius = size / 2;
        const innerRadius = radius * 0.62;
        const outerRadius = radius - 4;

        const g = svg
            .attr('width', size)
            .attr('height', size)
            .append('g')
            .attr('transform', `translate(${radius},${radius})`);

        // Build data: acknowledged vs pending vs not required
        const pieData: { value: number; color: string }[] = [];

        if (this.totalAcked > 0) pieData.push({ value: this.totalAcked, color: '#43a047' });
        if (this.totalPending > 0) pieData.push({ value: this.totalPending, color: '#f9a825' });
        if (this.totalNotRequired > 0) pieData.push({ value: this.totalNotRequired, color: '#90a4ae' });

        // If no data, show empty ring
        if (pieData.length === 0) {
            pieData.push({ value: 1, color: '#e0e0e0' });
        }

        const pie = d3
            .pie<{ value: number; color: string }>()
            .value((d) => d.value)
            .sort(null)
            .padAngle(0.02);

        const arc = d3
            .arc<d3.PieArcDatum<{ value: number; color: string }>>()
            .innerRadius(innerRadius)
            .outerRadius(outerRadius)
            .cornerRadius(3);

        // Draw arcs
        g.selectAll('path')
            .data(pie(pieData))
            .enter()
            .append('path')
            .attr('d', arc)
            .attr('fill', (d) => d.data.color)
            .attr('opacity', 0)
            .transition()
            .duration(600)
            .attr('opacity', 1)
            .attrTween('d', function (d) {
                const interp = d3.interpolate(
                    { startAngle: d.startAngle, endAngle: d.startAngle } as d3.PieArcDatum<{
                        value: number;
                        color: string;
                    }>,
                    d
                );
                return (t: number) => arc(interp(t)) || '';
            });

        this.chartDrawn = true;
    }
}
